import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Edit, DollarSign, Building2, Trash2 } from 'lucide-react';
import api from '../../lib/api';

const CustomPricingManager = () => {
  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingOrg, setEditingOrg] = useState(null);
  const [showDialog, setShowDialog] = useState(false);
  const [formData, setFormData] = useState({
    setup_fee: '',
    monthly_fee: '',
    min_monthly: '',
    max_monthly: '',
    notes: ''
  });

  useEffect(() => {
    fetchOrganizations();
  }, []);

  const fetchOrganizations = async () => {
    try {
      const response = await api.get('/admin/organizations');
      setOrganizations(response.data.organizations || []);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching organizations:', error);
      toast.error('Failed to load organizations');
      setLoading(false);
    }
  };

  const handleEditPricing = (org) => {
    setEditingOrg(org);
    const pricing = org.pricing || {};
    setFormData({
      setup_fee: pricing.setup_fee || '',
      monthly_fee: pricing.monthly_fee || pricing.min_monthly || '',
      min_monthly: pricing.min_monthly || '',
      max_monthly: pricing.max_monthly || '',
      notes: pricing.custom_notes || ''
    });
    setShowDialog(true);
  };

  const handleSaveCustomPricing = async () => {
    try {
      await api.post(`/admin/organizations/${editingOrg.id}/custom-pricing`, formData);
      toast.success('Custom pricing set successfully');
      setShowDialog(false);
      fetchOrganizations(); // Refresh list
    } catch (error) {
      console.error('Error setting custom pricing:', error);
      toast.error('Failed to set custom pricing');
    }
  };

  const handleRemoveCustomPricing = async (orgId) => {
    if (!confirm('Remove custom pricing and revert to platform defaults?')) return;
    
    try {
      await api.delete(`/admin/organizations/${orgId}/custom-pricing`);
      toast.success('Reverted to platform defaults');
      fetchOrganizations(); // Refresh list
    } catch (error) {
      console.error('Error removing custom pricing:', error);
      toast.error('Failed to remove custom pricing');
    }
  };

  const getOrgTypeColor = (type) => {
    const colors = {
      landlord: 'bg-blue-100 text-blue-800',
      hoa: 'bg-green-100 text-green-800',
      property_manager: 'bg-purple-100 text-purple-800'
    };
    return colors[type] || 'bg-gray-100 text-gray-800';
  };

  if (loading) {
    return (
      <div className="flex justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Custom Pricing Management</h2>
        <p className="text-gray-600">
          Set custom subscription rates for specific organizations. Custom rates override platform defaults.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {organizations.map((org) => (
          <Card key={org.id}>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <Building2 className="h-5 w-5 text-gray-500" />
                  <CardTitle className="text-lg">{org.name}</CardTitle>
                </div>
                <Badge className={getOrgTypeColor(org.type)}>
                  {org.type?.replace('_', ' ').toUpperCase()}
                </Badge>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-600">Pricing Type:</span>
                  <Badge className={org.pricing?.pricing_type === 'custom' ? 'bg-orange-100 text-orange-800' : 'bg-gray-100 text-gray-800'}>
                    {org.pricing?.pricing_type === 'custom' ? 'Custom' : 'Platform Default'}
                  </Badge>
                </div>
                {org.pricing?.setup_fee !== undefined && (
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600">Setup Fee:</span>
                    <span className="font-medium">${org.pricing.setup_fee}</span>
                  </div>
                )}
                {org.pricing?.monthly_fee !== undefined && (
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600">Monthly Fee:</span>
                    <span className="font-medium">${org.pricing.monthly_fee}</span>
                  </div>
                )}
                {org.pricing?.min_monthly !== undefined && org.pricing?.max_monthly !== undefined && (
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600">Monthly Range:</span>
                    <span className="font-medium">${org.pricing.min_monthly} - ${org.pricing.max_monthly}</span>
                  </div>
                )}
                {org.pricing?.custom_notes && (
                  <div className="text-xs text-gray-500 mt-2 p-2 bg-gray-50 rounded">
                    {org.pricing.custom_notes}
                  </div>
                )}
              </div>

              <div className="flex space-x-2 pt-4 border-t">
                <Button 
                  variant="outline" 
                  className="flex-1"
                  onClick={() => handleEditPricing(org)}
                >
                  <Edit className="h-4 w-4 mr-2" />
                  {org.pricing?.pricing_type === 'custom' ? 'Edit' : 'Set Custom'}
                </Button>
                {org.pricing?.pricing_type === 'custom' && (
                  <Button
                    variant="destructive"
                    onClick={() => handleRemoveCustomPricing(org.id)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Custom Pricing Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>
              Set Custom Pricing for {editingOrg?.name}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={(e) => { e.preventDefault(); handleSaveCustomPricing(); }} className="space-y-4">
            <div className="bg-blue-50 border border-blue-200 rounded p-3">
              <p className="text-sm text-blue-800">
                Custom pricing overrides platform defaults. Leave fields empty to use default values.
              </p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Setup Fee ($)</Label>
                <Input
                  type="number"
                  step="0.01"
                  placeholder="e.g. 500"
                  value={formData.setup_fee}
                  onChange={(e) => setFormData({...formData, setup_fee: e.target.value})}
                />
              </div>
              <div>
                <Label>Monthly Fee ($)</Label>
                <Input
                  type="number"
                  step="0.01"
                  placeholder="e.g. 300"
                  value={formData.monthly_fee}
                  onChange={(e) => setFormData({...formData, monthly_fee: e.target.value})}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Min Monthly ($)</Label>
                <Input
                  type="number"
                  step="0.01"
                  placeholder="e.g. 200"
                  value={formData.min_monthly}
                  onChange={(e) => setFormData({...formData, min_monthly: e.target.value})}
                />
              </div>
              <div>
                <Label>Max Monthly ($)</Label>
                <Input
                  type="number"
                  step="0.01"
                  placeholder="e.g. 1200"
                  value={formData.max_monthly}
                  onChange={(e) => setFormData({...formData, max_monthly: e.target.value})}
                />
              </div>
            </div>

            <div>
              <Label>Notes (Optional)</Label>
              <Input
                type="text"
                placeholder="Custom pricing reason or notes"
                value={formData.notes}
                onChange={(e) => setFormData({...formData, notes: e.target.value})}
              />
            </div>

            <div className="flex justify-end space-x-2 pt-4">
              <Button type="button" variant="outline" onClick={() => setShowDialog(false)}>
                Cancel
              </Button>
              <Button type="submit" className="bg-emerald-600 hover:bg-emerald-700">
                Save Custom Pricing
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CustomPricingManager;
